import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { shopService, batchService } from '../../services/index.js';
import {
  LoadingSpinner,
  ShopMap,
  ConfirmModal,
  SendCampaignModal,
  HelpTip,
} from '../../components/index.js';
import { API_URL } from '../../utils/constants.js';
import { cryptoEnabled, decryptPayload, APP_TOKEN } from '../../services/crypto.js';
import { postToParent, isEmbedded, getAppHost } from '../../utils/parentBridge.js';
import './ShopDetail.css';

// Stable per-tab client id so the SSE stream can skip echoing this client's
// own searches back to it (only searches from *other* sources update the map).
const makeClientId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `c_${Math.floor(performance.now())}_${performance.now()}`;

// Fallback appHost used when /shops/:id is opened directly (not via /embed).
// Useful for local development of the send-to-NCA flow without the iframe.
const DEV_APP_HOST = 'https://app.siauto.pro';

// Sorting is FIXED and not configurable: results always come back nearest
// first. The "Sort By" / "Order" selectors were removed from the panel so a
// search can never prioritize far-away houses over the ones next to the shop.
const FIXED_SORT = { sortBy: 'distance', sortOrder: 'asc' };

// Users didn't realize they can run SEVERAL searches over different spots of
// the map (each one becomes its own batch). We surface it with a "?" help
// popover next to Find New Customer, auto-opened once right after the first
// successful search — the moment it becomes actionable. This key remembers
// that auto-open already happened (the button still opens it on demand).
const MULTI_SEARCH_HINT_KEY = 'postcards:hint:multi-search-areas';

const hintAlreadyShown = () => {
  try {
    return localStorage.getItem(MULTI_SEARCH_HINT_KEY) === '1';
  } catch {
    return true; // storage blocked (private mode / iframe) → don't nag
  }
};

const markHintShown = () => {
  try {
    localStorage.setItem(MULTI_SEARCH_HINT_KEY, '1');
  } catch {
    /* ignore — the hint is still reachable from the "?" button */
  }
};

// The DB stores an internal lifecycle enum ('draft' | 'finalized' | 'exported')
// that leaked straight into the Batches table. "Draft" reads like an unfinished
// document the user still has to write, when it actually means "these addresses
// are collected and waiting to be mailed" — so it's phrased as the next action.
// The enum itself is untouched (API/scripts depend on it); this is display only.
const BATCH_STATUS_LABELS = {
  draft: 'Ready to Send',
  finalized: 'Ready to Send',
  exported: 'Sent',
};

const batchStatusLabel = (status) => BATCH_STATUS_LABELS[status] || status || '—';

const slog = (...args) => console.log('[postcards/shop]', ...args);

export const ShopDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const clientIdRef = useRef(makeClientId());
  const hydratedRef = useRef(false);

  // Initial config read from the URL query string AND the router state that
  // /embed forwards (location.state.campaignParams / .view). Query string wins
  // over router state. Lets the parent open the shop with the Find New Customer
  // panel pre-filled (+ optional autoRun) and/or in "campaign view" (map-only).
  const initConfig = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const sp = location.state?.campaignParams || {};
    const pick = (key) => {
      const v = qs.get(key);
      return v != null ? v : sp[key];
    };

    const viewRaw =
      qs.get('view') ||
      location.state?.view ||
      (qs.get('mapOnly') === '1' || qs.get('panel') === 'hidden'
        ? 'campaign'
        : null) ||
      (location.state?.mapOnly ? 'campaign' : null);

    return {
      mode: pick('mode'),
      radiusMiles: pick('radiusMiles'),
      searchZipCode: pick('searchZipCode'),
      searchCity: pick('searchCity'),
      searchState: pick('searchState'),
      searchRadius: pick('searchRadius'),
      maxRecords: pick('maxRecords'),
      zipCodes: pick('zipCodes'),
      excludeZipCodes: pick('excludeZipCodes'),
      cities: pick('cities'),
      excludeCities: pick('excludeCities'),
      includeUsed: pick('includeUsed'),
      campaignName: pick('campaignName'),
      autoRun: pick('autoRun'),
      showCustomers: pick('showCustomers') ?? pick('showExistingCustomers'),
      view: viewRaw === 'campaign' ? 'campaign' : 'full',
    };
  }, [location.search, location.state]);

  // Campaign view = hide the Find New Customer search panel; show only the
  // map, the Batches table and the Send Campaign to NCA button.
  const isCampaignView = initConfig.view === 'campaign';

  // Resolve appHost and surface exactly which source it came from
  const fromState = location.state?.appHost || '';
  // Reads the dedicated `appHost` key OR falls back to `usr.AppHost`.
  const fromStorage = getAppHost();
  const appHost = fromState || fromStorage || DEV_APP_HOST;
  const appHostSource = fromState
    ? 'location.state (embed flow)'
    : fromStorage
      ? 'localStorage (cached embed)'
      : 'DEV_APP_HOST fallback';

  slog('mount', {
    shopId: id,
    pathname: location.pathname,
    appHost,
    appHostSource,
    hasLocationState: !!location.state,
    readOnly: location.state?.readOnly,
  });

  const [shop, setShop] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchAddresses, setBatchAddresses] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  // Postcard balance from Service Intel (NcaPostcardsPurchasedQty), decremented
  // locally as batches are created. Caps how many records a search may request.
  const [quota, setQuota] = useState(null);
  // Existing-customer marker visibility. Default shown; the parent can toggle it
  // live via postMessage (see the postcards:existing-customers listener below),
  // or set an initial value via ?showCustomers=0.
  const [showCustomers, setShowCustomers] = useState(() => {
    const v = initConfig.showCustomers;
    return !(v === 'false' || v === '0' || v === false);
  });
  const [searchResults, setSearchResults] = useState(null);
  // Movable search-area center (when the user drags it on the map). null = the
  // search area sits on the shop. The shop point itself never moves.
  const [searchArea, setSearchArea] = useState(null);
  // Point SI asked to focus/highlight (full address object or {lat,lng}).
  const [focusAddress, setFocusAddress] = useState(null);
  const embedded = isEmbedded();

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  // Batches selected (in the Batches table) to be sent to NCA as one campaign
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  // Find New Customer form. Shown as a NON-blocking overlay on the top-left of
  // the map (not a popup), so the user can interact with the map while it stays
  // visible. Open by default in the (non-embedded/creation) shop view.
  const [findCustomerModalOpen, setFindCustomerModalOpen] = useState(true);
  // "You can search several areas" help popover (next to Find New Customer).
  const [helpOpen, setHelpOpen] = useState(false);

  const [viewingBatch, setViewingBatch] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Soft "you already searched here" alert (DUPLICATE_SEARCH_AREA) — it's not a
  // failure the user caused, it's a nudge to move the search area.
  const [areaWarning, setAreaWarning] = useState('');
  const [radiusInput, setRadiusInput] = useState('');
  const [includeUsed, setIncludeUsed] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showMap, setShowMap] = useState(true);

  // Campaign name for NCA integration
  const [campaignName, setCampaignName] = useState('');

  // Search mode: 'radius' (from shop) or 'location' (by ZIP/city)
  const [searchMode, setSearchMode] = useState('radius');

  // Location search state
  const [locationSearch, setLocationSearch] = useState({
    searchZipCode: '',
    searchCity: '',
    searchState: '',
    searchRadius: 1,
  });

  // Search filters state
  const [filters, setFilters] = useState({
    maxRecords: 100,
    // Address type is FIXED to SFH (houses only) — the selector was removed from
    // the panel. 'houses' maps to includeApartments:false / onlyApartments:false
    // server-side (SuiteCount-based classification).
    addressType: 'houses',
    zipCodes: '',
    excludeZipCodes: '',
    cities: '',
    excludeCities: '',
  });

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
  });

  // Update location search value
  const updateLocationSearch = (key, value) => {
    setLocationSearch((prev) => ({ ...prev, [key]: value }));
  };

  // Show confirm modal
  const showConfirmModal = ({ title, message, type = 'warning', onConfirm }) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm,
    });
  };

  // Close confirm modal
  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Update filter value
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Hard ceiling for Max Records: the shop's postcard balance. Null when the
  // balance doesn't apply (shop has no locationId) or couldn't be established
  // (Service Intel unreachable) — in that case we don't advertise a limit we
  // can't back up, and the backend decides whether to allow the search.
  const quotaMax =
    quota?.applicable && quota?.enforced && Number.isFinite(Number(quota.remaining))
      ? Number(quota.remaining)
      : null;
  const outOfPostcards = quotaMax === 0;

  // The postcard balance IS the Max Records value: the field starts at the full
  // available balance and is only editable DOWNWARD. Seeded once the balance
  // arrives (it loads after the initial render, so a hardcoded default would sit
  // there showing a number the client can't actually mail).
  //
  // After seeding, a refresh only ever LOWERS the value — it must never raise a
  // smaller number the user deliberately typed. `maxRecordsSeededRef` is also
  // set by the param hydration below, so an explicit `maxRecords` from Service
  // Intel isn't overwritten by the balance (it still gets clamped to it).
  const maxRecordsSeededRef = useRef(false);
  useEffect(() => {
    if (quotaMax == null) return;
    setFilters((prev) => {
      if (!maxRecordsSeededRef.current) {
        maxRecordsSeededRef.current = true;
        return { ...prev, maxRecords: quotaMax };
      }
      const current = Number(prev.maxRecords);
      return Number.isFinite(current) && current > quotaMax
        ? { ...prev, maxRecords: quotaMax }
        : prev;
    });
  }, [quotaMax]);

  // Re-read the balance (used after a batch is created and when a quota error
  // comes back — a refill on Service Intel's side shows up here).
  const refreshQuota = async () => {
    try {
      const res = await shopService.getPostcardQuota(id);
      setQuota(res.data.quota || null);
    } catch (_) {
      /* non-fatal: the panel just keeps the previous number */
    }
  };

  // Fetch shop data
  const fetchShop = async () => {
    try {
      setLoading(true);
      const [shopRes, batchesRes, batchAddressesRes, customersRes, quotaRes] =
        await Promise.all([
          shopService.getShopById(id),
          shopService.getBatches(id),
          shopService.getBatchAddressesForMap(id),
          shopService.getCustomersForMap(id).catch(() => null),
          // Never let a quota/Service Intel hiccup block the page from loading.
          shopService.getPostcardQuota(id).catch(() => null),
        ]);
      setShop(shopRes.data.shop);
      setBatches(batchesRes.data.batches);
      setBatchAddresses(batchAddressesRes.data.addresses || []);
      setCustomerAddresses(customersRes?.data?.customers || []);
      setQuota(quotaRes?.data?.quota || null);
      setRadiusInput(shopRes.data.shop.radiusMiles.toString());
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load shop');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShop();
  }, [id]);

  // Real-time map updates: subscribe to this shop's SSE stream. When a search
  // is run for this shop from ANY source (e.g. the external API), the backend
  // pushes a `search-updated` event and the map refreshes live. Searches this
  // very client triggers are skipped server-side via the clientId (cid).
  useEffect(() => {
    if (!id) return undefined;

    const params = new URLSearchParams();
    if (APP_TOKEN) params.set('token', APP_TOKEN);
    params.set('cid', clientIdRef.current);
    const url = `${API_URL}/shops/${id}/stream?${params.toString()}`;

    const es = new EventSource(url);

    es.addEventListener('search-updated', async (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const payload =
          cryptoEnabled() && parsed && typeof parsed.p === 'string'
            ? await decryptPayload(parsed.p)
            : parsed;
        slog('SSE search-updated', { addresses: payload?.addresses?.length });
        applySearchResults(payload, { fromApi: true });
      } catch (err) {
        slog('SSE search-updated failed', err.message);
      }
    });

    // Radius / shop changes pushed from the API: update the shop so the map's
    // radius circle and marker move in real time — without running a search.
    es.addEventListener('shop-updated', async (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const payload =
          cryptoEnabled() && parsed && typeof parsed.p === 'string'
            ? await decryptPayload(parsed.p)
            : parsed;
        if (payload?.shop) {
          slog('SSE shop-updated', { radiusMiles: payload.shop.radiusMiles });
          setShop(payload.shop);
          setRadiusInput(String(payload.shop.radiusMiles));
        }
      } catch (err) {
        slog('SSE shop-updated failed', err.message);
      }
    });

    // Existing customers pushed from Service Intel: repaint the customer
    // markers live without a reload (the ingestion endpoint emits this).
    es.addEventListener('customers-updated', async (e) => {
      try {
        const parsed = JSON.parse(e.data);
        const payload =
          cryptoEnabled() && parsed && typeof parsed.p === 'string'
            ? await decryptPayload(parsed.p)
            : parsed;
        if (payload?.customers) {
          slog('SSE customers-updated', { customers: payload.customers.length });
          setCustomerAddresses(payload.customers);
        }
      } catch (err) {
        slog('SSE customers-updated failed', err.message);
      }
    });

    es.onerror = () => {
      // EventSource reconnects automatically; just log for visibility.
      slog('SSE connection error (will auto-retry)');
    };

    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Live radius edit pushed by the parent (Service Intel) via postMessage and
  // re-broadcast by <ParentBridge> as a `postcards:radius-update` CustomEvent.
  // PATCH the radius + redraw the coverage circle only — no search, no Melissa.
  // Works in both the full shop view and view=campaign (map-only).
  useEffect(() => {
    if (!id) return undefined;

    const onRadiusUpdate = async (e) => {
      const radiusMiles = Number(e.detail?.radiusMiles);
      if (!Number.isFinite(radiusMiles) || radiusMiles < 0.1 || radiusMiles > 50) return;
      try {
        slog('parent radius-update', { radiusMiles });
        const res = await shopService.updateShopRadius(id, radiusMiles, clientIdRef.current);
        setShop(res.data.shop);
        setRadiusInput(String(res.data.shop.radiusMiles));
      } catch (err) {
        slog('parent radius-update failed', err.message);
      }
    };

    window.addEventListener('postcards:radius-update', onRadiusUpdate);
    return () => window.removeEventListener('postcards:radius-update', onRadiusUpdate);
  }, [id]);

  // Live show/hide of existing-customer markers, controlled by the parent via
  // postMessage and re-broadcast by <ParentBridge> as a CustomEvent. Pure UI
  // toggle — no backend call, instant.
  useEffect(() => {
    const onToggleCustomers = (e) => {
      const show = e.detail?.show !== false;
      slog('parent existing-customers', { show });
      setShowCustomers(show);
    };
    window.addEventListener('postcards:existing-customers', onToggleCustomers);
    return () => window.removeEventListener('postcards:existing-customers', onToggleCustomers);
  }, []);

  // SI asked to focus/highlight a specific point. Match it (by melissaAddressKey,
  // else by exact coords) against the current new-customers / customers / batch
  // pools so we can show its full popup; fall back to plain coords.
  const focusPoolsRef = useRef({ results: null, customers: [], batch: [] });
  useEffect(() => {
    focusPoolsRef.current = {
      results: searchResults,
      customers: customerAddresses,
      batch: batchAddresses,
    };
  }, [searchResults, customerAddresses, batchAddresses]);

  useEffect(() => {
    const onFocus = (e) => {
      const d = e.detail || {};
      const { results, customers, batch } = focusPoolsRef.current;
      const pool = [...(results?.addresses || []), ...customers, ...batch];
      let match = null;
      if (d.melissaAddressKey) {
        match = pool.find((a) => a.melissaAddressKey === d.melissaAddressKey) || null;
      }
      if (!match && d.latitude != null && d.longitude != null) {
        match = pool.find(
          (a) =>
            Math.abs(a.latitude - d.latitude) < 1e-6 &&
            Math.abs(a.longitude - d.longitude) < 1e-6
        ) || null;
      }
      if (match) {
        slog('focus-address matched', { key: match.melissaAddressKey });
        setFocusAddress(match);
      } else if (d.latitude != null && d.longitude != null) {
        slog('focus-address by coords (no match)');
        setFocusAddress({ latitude: d.latitude, longitude: d.longitude });
      } else {
        slog('focus-address ignored — no key match and no coords');
      }
    };
    window.addEventListener('postcards:focus-address', onFocus);
    return () => window.removeEventListener('postcards:focus-address', onFocus);
  }, []);

  // Hydrate the Find New Customer panel from URL/postMessage params once the
  // shop is loaded. Optionally auto-runs the search (autoRun=true). Runs once.
  useEffect(() => {
    if (hydratedRef.current || !shop) return;
    hydratedRef.current = true;

    const c = initConfig;
    const has = (v) => v !== undefined && v !== null && v !== '';
    const truthy = (v) => v === true || v === 'true' || v === '1';

    const nextMode =
      c.mode === 'location' || c.mode === 'radius' ? c.mode : searchMode;

    // ONLY the keys the parent actually sent. Spreading the whole `filters`
    // closure here used to clobber state written by other effects with a stale
    // snapshot — notably the postcard-balance seeding, which is why Max Records
    // kept showing its old default while the hint already said "3 available".
    // addressType comes from the panel selector; sorting is fixed (FIXED_SORT).
    const filterOverrides = {
      // An explicit maxRecords from SI is honored but still clamped to the
      // postcard balance, and marks the field as seeded so the balance effect
      // doesn't overwrite it (it may still lower it later).
      ...(has(c.maxRecords)
        ? {
            maxRecords:
              quotaMax != null
                ? Math.min(Number(c.maxRecords), quotaMax)
                : Number(c.maxRecords),
          }
        : {}),
      ...(has(c.zipCodes) ? { zipCodes: c.zipCodes } : {}),
      ...(has(c.excludeZipCodes) ? { excludeZipCodes: c.excludeZipCodes } : {}),
      ...(has(c.cities) ? { cities: c.cities } : {}),
      ...(has(c.excludeCities) ? { excludeCities: c.excludeCities } : {}),
    };

    // For the autoRun below we need concrete values now. With no maxRecords
    // param, the balance is the value (matching what the seeding effect put in
    // state), not the possibly-stale closure default.
    const nextFilters = {
      ...filters,
      ...filterOverrides,
      ...(has(c.maxRecords) || quotaMax == null
        ? {}
        : { maxRecords: Math.min(Number(filters.maxRecords) || quotaMax, quotaMax) }),
    };

    const nextLocation = {
      ...locationSearch,
      ...(has(c.searchZipCode) ? { searchZipCode: c.searchZipCode } : {}),
      ...(has(c.searchCity) ? { searchCity: c.searchCity } : {}),
      ...(has(c.searchState) ? { searchState: c.searchState } : {}),
      ...(has(c.searchRadius) ? { searchRadius: Number(c.searchRadius) } : {}),
    };

    const nextRadius = has(c.radiusMiles) ? String(c.radiusMiles) : radiusInput;

    // Reflect everything in the panel state.
    setSearchMode(nextMode);
    // Functional merge so this never overwrites concurrent state updates.
    setFilters((prev) => ({ ...prev, ...filterOverrides }));
    if (has(c.maxRecords)) maxRecordsSeededRef.current = true;
    setLocationSearch(nextLocation);
    if (has(c.includeUsed)) setIncludeUsed(truthy(c.includeUsed));
    if (has(c.campaignName)) setCampaignName(c.campaignName);

    const anyParam =
      has(c.mode) ||
      has(c.maxRecords) ||
      has(c.zipCodes) ||
      has(c.excludeZipCodes) ||
      has(c.cities) ||
      has(c.excludeCities) ||
      has(c.searchZipCode) ||
      has(c.searchCity) ||
      has(c.searchState) ||
      has(c.searchRadius) ||
      has(c.radiusMiles);
    if (anyParam) slog('panel hydrated from params', c);

    (async () => {
      // Persist radiusMiles for radius mode so the search uses it (radius
      // searches read shop.radiusMiles server-side, not the local input).
      if (has(c.radiusMiles)) {
        const r = Number(c.radiusMiles);
        setRadiusInput(String(r));
        if (
          nextMode === 'radius' &&
          !isNaN(r) &&
          r >= 0.1 &&
          r <= 50 &&
          r !== shop.radiusMiles
        ) {
          try {
            const res = await shopService.updateShopRadius(id, r, clientIdRef.current);
            setShop(res.data.shop);
          } catch (err) {
            slog('radius hydrate failed', err.message);
          }
        }
      }

      if (!truthy(c.autoRun)) return;

      const canRun =
        nextMode === 'radius'
          ? shop.isGeocoded
          : !!(
              nextLocation.searchZipCode ||
              (nextLocation.searchCity && nextLocation.searchState)
            );
      if (canRun) {
        await runSearch({
          mode: nextMode,
          flt: nextFilters,
          loc: nextLocation,
          radius: nextRadius,
        });
      } else {
        slog('autoRun skipped — shop not geocoded / missing location inputs');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  // Geocode shop - actual API call
  const executeGeocode = async () => {
    try {
      setGeocoding(true);
      setError('');
      const res = await shopService.geocodeShop(id);
      setShop(res.data.shop);
      setSuccess('Shop geocoded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to geocode shop');
    } finally {
      setGeocoding(false);
    }
  };

  // Geocode shop - show confirmation first
  const handleGeocode = () => {
    showConfirmModal({
      title: 'Geocode Shop',
      message:
        'This will use Melissa API credits to geocode the shop address. Do you want to continue?',
      type: 'info',
      onConfirm: executeGeocode,
    });
  };

  // The user dragged the SEARCH AREA (the shop point stays fixed). This ONLY
  // moves the coverage circle — it does NOT run a search. The search is started
  // from the Find New Customer modal and will use this center (see executeSearch).
  const handleSearchAreaChange = ({ latitude, longitude }) => {
    setSearchArea({ latitude, longitude });
    // Moving the area is exactly the fix the duplicate-area alert asks for.
    setAreaWarning('');
  };

  // Update radius
  const handleUpdateRadius = async () => {
    const newRadius = parseFloat(radiusInput);
    if (isNaN(newRadius) || newRadius < 0.1 || newRadius > 50) {
      setError('Radius must be between 0.1 and 50 miles');
      return;
    }

    try {
      const res = await shopService.updateShopRadius(id, newRadius, clientIdRef.current);
      setShop(res.data.shop);
      setSuccess('Radius updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update radius');
    }
  };

  // Apply a search result to the page: render the result set and pre-select
  // all new (not previously used) addresses. Shared by the local search flow
  // and by the SSE live-update push (fromApi=true).
  const applySearchResults = (payload, { fromApi = false } = {}) => {
    if (!payload || !Array.isArray(payload.addresses)) return;
    setSearchResults(payload);
    if (fromApi) {
      setFindCustomerModalOpen(false);
      setSuccess(`Map updated from API — ${payload.addresses.length} addresses`);
      setTimeout(() => setSuccess(''), 4000);
    }
    // Send the new-customer list UP to Service Intel so it can build its picker
    // and later ask us to focus one (postcards:focus-address). Only the fields
    // SI needs to identify + display a point.
    postToParent({
      type: 'search-results',
      data: {
        shopId: id,
        searchMode: payload.searchMode || null,
        count: payload.addresses.length,
        addresses: payload.addresses.map((a) => ({
          melissaAddressKey: a.melissaAddressKey,
          addressLine1: a.addressLine1,
          addressLine2: a.addressLine2 || '',
          city: a.city,
          state: a.state,
          zipCode: a.zipCode,
          latitude: a.latitude,
          longitude: a.longitude,
          distance: a.distance,
          suiteCount: a.suiteCount || 0,
          isPreviouslyUsed: !!a.isPreviouslyUsed,
        })),
      },
    });
  };

  // Run a search with EXPLICIT values (mode/filters/location/radius). Used by
  // the panel (executeSearch, current state) and by the param-hydration
  // autoRun (passes hydrated values so it doesn't wait for state to flush).
  // Address type comes from the panel selector → includeApartments/onlyApartments.
  const runSearch = async ({ mode, flt, loc, radius }) => {
    let success = false;
    try {
      setSearching(true);
      setError('');
      setAreaWarning('');
      setSearchResults(null);

      let res;

      if (mode === 'radius') {
        res = await shopService.searchAddresses(id, {
          maxRecords: flt.maxRecords,
          includeApartments: flt.addressType !== 'houses',
          onlyApartments: flt.addressType === 'apartments',
          zipCodes: flt.zipCodes || undefined,
          excludeZipCodes: flt.excludeZipCodes || undefined,
          cities: flt.cities || undefined,
          excludeCities: flt.excludeCities || undefined,
          ...FIXED_SORT,
          clientId: clientIdRef.current,
        });
      } else {
        res = await shopService.searchAddressesByLocation(id, {
          // Explicit coords (dragged search area) take precedence over ZIP/city.
          latitude: loc.latitude ?? undefined,
          longitude: loc.longitude ?? undefined,
          searchZipCode: loc.searchZipCode,
          searchCity: loc.searchCity,
          searchState: loc.searchState,
          searchRadius: parseFloat(radius) || shop.radiusMiles,
          maxRecords: flt.maxRecords,
          includeApartments: flt.addressType !== 'houses',
          onlyApartments: flt.addressType === 'apartments',
          ...FIXED_SORT,
          clientId: clientIdRef.current,
        });
      }

      applySearchResults(res.data);
      // Auto-create a batch from every NEW (not previously used) address — no
      // manual selection. Then refresh so they show as "In Batch".
      await autoCreateBatchFromResults(res.data);
      success = true;

      // Teachable moment: the user just got their first batch — tell them (once)
      // that they can move the search area and search other spots too.
      if (!hintAlreadyShown()) {
        markHintShown();
        setHelpOpen(true);
      }
    } catch (err) {
      // "Already searched this exact area" is guidance, not an error: show it as
      // a warning and open the help so they learn they can move the area.
      if (err.errorCode === 'DUPLICATE_SEARCH_AREA') {
        setAreaWarning(err.message);
        setHelpOpen(true);
      } else if (
        err.errorCode === 'NO_POSTCARD_QUOTA' ||
        err.errorCode === 'POSTCARD_QUOTA_EXCEEDED'
      ) {
        // Out of postcards is a business limit, not a failure — show it as a
        // warning and re-read the balance so a fresh purchase unblocks the user.
        setAreaWarning(err.message);
        refreshQuota();
      } else {
        setError(err.message || 'Failed to search addresses');
      }
    } finally {
      setSearching(false);
    }
    return success;
  };

  // Execute search from the current panel state. Returns true on success.
  // If the user dragged the search area, a radius search is run around THAT
  // center (location-by-coords) instead of the shop's own location.
  const executeSearch = () => {
    if (searchMode === 'radius' && searchArea) {
      return runSearch({
        mode: 'location',
        flt: filters,
        loc: { latitude: searchArea.latitude, longitude: searchArea.longitude },
        radius: radiusInput,
      });
    }
    return runSearch({
      mode: searchMode,
      flt: filters,
      loc: locationSearch,
      radius: radiusInput,
    });
  };

  // Search addresses — direct call to the API, no confirmation modal.
  // On success it closes the Find New Customer modal so results are
  // visible in the page below.
  const handleSearch = async () => {
    // Validate based on search mode
    if (searchMode === 'radius') {
      if (!shop.isGeocoded) {
        setError('Please geocode the shop first');
        return;
      }
    } else {
      // Location mode - validate inputs
      if (
        !locationSearch.searchZipCode &&
        (!locationSearch.searchCity || !locationSearch.searchState)
      ) {
        setError('Please enter a ZIP code, or city and state');
        return;
      }
    }

    const ok = await executeSearch();
    if (ok) {
      setFindCustomerModalOpen(false);
    }
  };

  // Reset filters to defaults
  const resetFilters = () => {
    setFilters({
      // Back to the full postcard balance (the ceiling), not a hardcoded number.
      maxRecords: quotaMax ?? 100,
      addressType: 'all', // default: SFH + Apartments (user-selectable in the panel)
      zipCodes: '',
      excludeZipCodes: '',
      cities: '',
      excludeCities: '',
    });
  };

  // Auto-create a batch from the NEW addresses of a search result. No manual
  // selection — every address that isn't already used becomes part of a new
  // batch. The campaign name is NOT set here (it's only used when sending a
  // campaign to NCA). Refreshes batches + batch markers afterwards.
  const autoCreateBatchFromResults = async (payload) => {
    const newAddrs = (payload?.addresses || []).filter((a) => !a.isPreviouslyUsed);
    if (newAddrs.length === 0) {
      setSuccess('Search returned no new addresses (all previously used).');
      setTimeout(() => setSuccess(''), 3500);
      return;
    }
    try {
      setCreatingBatch(true);
      await shopService.createBatchFromSearch(id, {
        addresses: newAddrs,
        includesPreviouslyUsed: false,
        totalFound: payload.totalFound,
      });
      setSuccess(`Batch created automatically with ${newAddrs.length} new address(es).`);
      // Refresh batches + batch markers so the new addresses show as "In Batch".
      await fetchShop();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      // The batch is what actually consumes postcards, so this is where a short
      // balance surfaces. Warning, not error — and re-read the balance.
      if (err.errorCode === 'POSTCARD_QUOTA_EXCEEDED' || err.errorCode === 'NO_POSTCARD_QUOTA') {
        setAreaWarning(err.message);
        await refreshQuota();
      } else {
        setError(err.message || 'Failed to create batch');
      }
    } finally {
      setCreatingBatch(false);
    }
  };

  // Send a campaign (N batches) to NCA in a single operation
  const handleSendCampaign = async ({ campaignName, batchIds }) => {
    slog('handleSendCampaign called', {
      shopId: id,
      appHost,
      appHostSource,
      campaignName,
      batchIdsCount: batchIds.length,
      batchIds,
    });

    if (!appHost) {
      console.warn('[postcards/shop] ✗ appHost empty — aborting');
      setError('AppHost not available.');
      return;
    }

    try {
      setSendingCampaign(true);
      setError('');
      slog('calling batchService.sendCampaignToNCA...');
      const res = await batchService.sendCampaignToNCA(id, {
        appHost,
        campaignName,
        batchIds,
      });
      const nacId = res.data?.nacResult?.campaignId;
      const updatedBatches = res.data?.batches || [];
      slog('response received', {
        nacCampaignId: nacId,
        updatedBatchCount: updatedBatches.length,
      });

      if (!nacId) {
        console.warn('[postcards/shop] ✗ response missing campaignId');
        setError('NCA responded without a campaignId. Check backend logs.');
        return;
      }

      // Optimistic update — patch the sent batches in the local list
      setBatches((prev) =>
        prev.map((b) => {
          const updated = updatedBatches.find((u) => u._id === b._id);
          return updated
            ? {
                ...b,
                nacCampaignId: updated.nacCampaignId,
                campaignName: updated.campaignName,
                status: updated.status,
                exportedAt: updated.exportedAt,
              }
            : b;
        })
      );

      setSuccess(
        `Campaign "${campaignName}" sent — Campaign #${nacId} (${updatedBatches.length} batches)`
      );
      setCampaignModalOpen(false);
      setSelectedBatchIds([]);
      slog('✓ campaign sent successfully', { nacId });
      fetchShop();
      setTimeout(() => setSuccess(''), 4500);
    } catch (err) {
      console.error('[postcards/shop] ✗ send campaign failed', err);
      setError(err.message || 'Failed to send campaign to NCA');
    } finally {
      setSendingCampaign(false);
    }
  };

  // View batch addresses
  const handleViewBatch = async (batchId) => {
    try {
      setLoadingBatch(true);
      const res = await batchService.getBatchById(batchId);
      setViewingBatch(res.data.batch);
    } catch (err) {
      setError(err.message || 'Failed to load batch details');
    } finally {
      setLoadingBatch(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container-shopdetail">
        <LoadingSpinner size="large" message="Loading shop..." />
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="error-container-shopdetail">
        <p>Shop not found</p>
        <Link to="/shops" className="breadcrumb-link-shopdetail">
          Back to Shops
        </Link>
      </div>
    );
  }

  return (
    <div className="container-shopdetail">
      {/* Messages */}
      {error && <div className="error-shopdetail">{error}</div>}
      {success && <div className="success-shopdetail">{success}</div>}
      {/* Duplicate-area alert. Shown here only when the panel is closed (e.g. a
          parent-driven autoRun); otherwise it renders inside the panel, next to
          the controls the user has to change. */}
      {areaWarning && !findCustomerModalOpen && (
        <div className="area-warning-shopdetail">
          <span className="area-dot-shopdetail" />
          {areaWarning}
        </div>
      )}

      {/* Map (always full width — search filters live inside the Find New Customer modal) */}
      {(shop.isGeocoded || searchResults?.searchCenter) && (
        <div className="map-card-shopdetail map-full-shopdetail">
          <div className="map-header-shopdetail">
            {/* Title hidden inside the SI embed (the parent already shows it). */}
            {!embedded && (
              <h2 className="card-title-shopdetail">
                <svg
                  className="card-title-icon-shopdetail"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {searchResults?.searchMode === 'location'
                  ? `Location: ${searchResults.searchCenter?.city || searchResults.searchCenter?.zipCode}, ${searchResults.searchCenter?.state}`
                  : 'Map'}
              </h2>
            )}
            <div className="map-header-actions-shopdetail">
              {!isCampaignView && (
                <button
                  onClick={() => setFindCustomerModalOpen(true)}
                  className="find-customer-btn-shopdetail"
                  title="Find new customers in the area"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  Find New Customer
                </button>
              )}
              {!isCampaignView && (
                <HelpTip
                  isOpen={helpOpen}
                  onToggle={setHelpOpen}
                  title="You can search several areas"
                >
                  <ol>
                    <li>
                      Drag the <span className="area-dot-helptip" />
                      <strong>search area</strong> (the blue target) anywhere on the
                      map. The coverage circle follows it; the red shop pin never moves.
                    </li>
                    <li>
                      Click <strong>Find New Customer</strong> and search again — the
                      search runs around wherever you left the circle.
                    </li>
                    <li>
                      Each search creates <strong>its own batch</strong> and only picks
                      addresses that were never used before, so areas never overlap.
                      Repeat as many times as you need to cover several neighborhoods,
                      then send them all as one campaign.
                    </li>
                  </ol>
                </HelpTip>
              )}
            </div>
          </div>
          <ShopMap
            shop={shop}
            addresses={searchResults?.addresses || []}
            batchAddresses={batchAddresses}
            customerAddresses={customerAddresses}
            showCustomers={showCustomers}
            searchArea={searchArea}
            onSearchAreaChange={handleSearchAreaChange}
            focusAddress={focusAddress}
            searchCenter={searchResults?.searchCenter}
            searchRadius={
              Number(radiusInput) || searchResults?.searchRadius || shop?.radiusMiles
            }
            height="600px"
            fitToAll
          />
        </div>
      )}

      {/* Search Results (hidden in campaign view) */}
      {!isCampaignView && searchResults && (
        <div className="results-card-shopdetail">
          <div className="results-header-shopdetail">
            <h2 className="card-title-shopdetail">Search Results</h2>
            <div className="results-stats-shopdetail">
              <span className="stat-total-shopdetail">
                {searchResults.totalAfterFilters || searchResults.addresses.length} shown
                {searchResults.totalFound !== searchResults.totalAfterFilters && (
                  <span className="stat-filtered-shopdetail">
                    {' '}
                    (of {searchResults.totalFound} found)
                  </span>
                )}
              </span>
              <span className="stat-new-shopdetail">{searchResults.newAddressesCount} new</span>
              <span className="stat-used-shopdetail">{searchResults.previouslyUsedCount} used</span>
            </div>
          </div>

          {/* Applied Filters Summary */}
          {searchResults.filtersApplied && (
            <div className="filters-summary-shopdetail">
              <strong>Filters:</strong>
              {searchResults.filtersApplied.onlyApartments && (
                <span className="filter-tag-shopdetail">Apartments Only</span>
              )}
              {!searchResults.filtersApplied.includeApartments &&
                !searchResults.filtersApplied.onlyApartments && (
                  <span className="filter-tag-shopdetail">Houses Only</span>
                )}
              {searchResults.filtersApplied.zipCodes && (
                <span className="filter-tag-shopdetail">
                  ZIP: {searchResults.filtersApplied.zipCodes}
                </span>
              )}
              {searchResults.filtersApplied.excludeZipCodes && (
                <span className="filter-tag-shopdetail">
                  Excl ZIP: {searchResults.filtersApplied.excludeZipCodes}
                </span>
              )}
              {searchResults.filtersApplied.cities && (
                <span className="filter-tag-shopdetail">
                  Cities: {searchResults.filtersApplied.cities}
                </span>
              )}
              {searchResults.filtersApplied.excludeCities && (
                <span className="filter-tag-shopdetail">
                  Excl Cities: {searchResults.filtersApplied.excludeCities}
                </span>
              )}
              {/* Sorting is fixed (nearest first) — no tag, nothing to vary. */}
            </div>
          )}

          <div className="results-actions-shopdetail">
            <span className="auto-batch-note-shopdetail">
              New addresses were added automatically to a new batch (see Batches below).
            </span>
            {/* Nudge towards the next area — this is where users stop, not
                realizing the flow can be repeated over other spots. */}
            <button
              type="button"
              className="next-area-note-shopdetail"
              onClick={() => setHelpOpen(true)}
            >
              <span className="area-dot-shopdetail" />
              Want another area? Drag the search area on the map and search again.
            </button>
          </div>

          <div className="results-table-container-shopdetail">
            <table className="results-table-shopdetail">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>ZIP</th>
                  <th>Distance</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.addresses.map((addr) => (
                  <tr
                    key={addr.melissaAddressKey}
                    className={`result-row-shopdetail ${addr.isPreviouslyUsed ? 'used' : ''}`}
                  >
                    <td>{addr.addressLine1}</td>
                    <td>{addr.city}</td>
                    <td>{addr.state}</td>
                    <td>{addr.zipCode}</td>
                    <td>{addr.distance?.toFixed(3)} mi</td>
                    <td>
                      {addr.suiteCount > 0 ? (
                        <span
                          className="apt-badge-shopdetail"
                          title={`Apartment - ${addr.suiteCount} units`}
                        >
                          APT ({addr.suiteCount})
                        </span>
                      ) : (
                        <span className="sfh-badge-shopdetail" title="Single Family Home">
                          SFH
                        </span>
                      )}
                    </td>
                    <td>
                      {addr.isPreviouslyUsed ? (
                        <span className="used-badge-shopdetail">
                          <span className="badge-dot-shopdetail" />
                          Used
                        </span>
                      ) : (
                        <span className="new-badge-shopdetail">
                          <span className="badge-dot-shopdetail" />
                          New
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {/* Batches Section */}
      {batches.length > 0 && (
        <div className="card-shopdetail">
          <div className="batches-header-shopdetail">
            <h2 className="card-title-shopdetail">Batches</h2>
            {batches.some((b) => !b.nacCampaignId) && (
              <button
                onClick={() => setCampaignModalOpen(true)}
                className="send-list-btn-shopdetail"
                disabled={!appHost || selectedBatchIds.length === 0}
                title={
                  !appHost
                    ? 'AppHost not available'
                    : selectedBatchIds.length === 0
                      ? 'Select at least one batch to send'
                      : 'Send selected batches to NCA'
                }
              >
                Send Campaign
                {selectedBatchIds.length > 0 ? ` (${selectedBatchIds.length})` : ''}
              </button>
            )}
          </div>
          <div className="batches-table-container-shopdetail">
            <table className="batches-table-shopdetail">
              <thead>
                <tr>
                  <th>
                    {(() => {
                      const unsent = batches.filter((b) => !b.nacCampaignId);
                      const allSelected =
                        unsent.length > 0 &&
                        unsent.every((b) => selectedBatchIds.includes(b._id));
                      return (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          disabled={unsent.length === 0}
                          onChange={() =>
                            setSelectedBatchIds(
                              allSelected ? [] : unsent.map((b) => b._id)
                            )
                          }
                          title={
                            allSelected ? 'Deselect all' : 'Select all unsent batches'
                          }
                        />
                      );
                    })()}
                  </th>
                  <th>Batch #</th>
                  <th>Addresses</th>
                  <th>Campaign</th>
                  <th>NCA</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch._id}>
                    <td>
                      {!batch.nacCampaignId && (
                        <input
                          type="checkbox"
                          checked={selectedBatchIds.includes(batch._id)}
                          onChange={() =>
                            setSelectedBatchIds((prev) =>
                              prev.includes(batch._id)
                                ? prev.filter((x) => x !== batch._id)
                                : [...prev, batch._id]
                            )
                          }
                        />
                      )}
                    </td>
                    <td className="batch-number-shopdetail">{batch.batchNumber}</td>
                    <td>{batch.totalAddressesSelected}</td>
                    <td>{batch.campaignName || '—'}</td>
                    <td>
                      {batch.nacCampaignId ? (
                        <span className="nca-sent-badge-shopdetail">#{batch.nacCampaignId}</span>
                      ) : (
                        <span className="nca-pending-badge-shopdetail">Not sent</span>
                      )}
                    </td>
                    <td>
                      <span className={`batch-status-shopdetail ${batch.status}`}>
                        {batchStatusLabel(batch.status)}
                      </span>
                    </td>
                    <td>{new Date(batch.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="actions-cell-shopdetail">
                        <button
                          onClick={() => handleViewBatch(batch._id)}
                          className="view-btn-shopdetail"
                          title="View addresses"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Continue"
        cancelText="Cancel"
      />

      {/* Batch Addresses Modal */}
      {(viewingBatch || loadingBatch) && (
        <div
          className="batch-modal-backdrop-shopdetail"
          onClick={() => !loadingBatch && setViewingBatch(null)}
        >
          <div className="batch-modal-shopdetail" onClick={(e) => e.stopPropagation()}>
            <div className="batch-modal-header-shopdetail">
              <div className="batch-modal-title-shopdetail">
                <h3>{viewingBatch ? `Batch ${viewingBatch.batchNumber}` : 'Loading...'}</h3>
                {viewingBatch && (
                  <span className="batch-modal-count-shopdetail">
                    {viewingBatch.addresses?.length || 0} addresses
                  </span>
                )}
              </div>
              <button
                className="batch-modal-close-shopdetail"
                onClick={() => setViewingBatch(null)}
                disabled={loadingBatch}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="batch-modal-body-shopdetail">
              {loadingBatch ? (
                <div className="batch-modal-loading-shopdetail">
                  <LoadingSpinner size="medium" message="Loading addresses..." />
                </div>
              ) : viewingBatch?.addresses?.length > 0 ? (
                <table className="batch-modal-table-shopdetail">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Address</th>
                      <th>City</th>
                      <th>State</th>
                      <th>ZIP</th>
                      <th>Type</th>
                      <th>Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingBatch.addresses.map((addr, idx) => (
                      <tr key={addr._id || idx}>
                        <td>{idx + 1}</td>
                        <td>{addr.addressLine1}</td>
                        <td>{addr.city}</td>
                        <td>{addr.state}</td>
                        <td>{addr.zipCode}</td>
                        <td>
                          {addr.suiteCount > 0 ? (
                            <span
                              className="apt-badge-shopdetail"
                              title={`Apartment - ${addr.suiteCount} units`}
                            >
                              APT ({addr.suiteCount})
                            </span>
                          ) : (
                            <span className="sfh-badge-shopdetail" title="Single Family Home">
                              SFH
                            </span>
                          )}
                        </td>
                        <td>{(addr.distanceFromShop ?? addr.distance)?.toFixed(3)} mi</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="batch-modal-empty-shopdetail">No addresses found in this batch.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Find New Customer Modal — holds the full Search Addresses configuration */}
      {!isCampaignView && findCustomerModalOpen && (
        <div className="find-overlay-shopdetail">
          <div className="find-overlay-card-shopdetail">
            <div className="modal-header-findcustomer-shopdetail">
              <h2 className="modal-title-findcustomer-shopdetail">Find New Customer</h2>
              <button
                onClick={() => setFindCustomerModalOpen(false)}
                className="modal-close-findcustomer-shopdetail"
                disabled={searching}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body-findcustomer-shopdetail">
          {/* Search Radius */}
          <div className="radius-row-shopdetail">
            <label className="radius-label-shopdetail">Search Radius</label>
            <div className="radius-input-group-shopdetail">
              <input
                type="number"
                value={radiusInput}
                onChange={(e) => setRadiusInput(e.target.value)}
                min="0.1"
                max="50"
                step="0.5"
                className="radius-input-shopdetail"
              />
              <span className="radius-unit-shopdetail">mi</span>
            </div>
          </div>

          {!shop.isGeocoded && (
            <button onClick={handleGeocode} disabled={geocoding} className="geocode-btn-shopdetail">
              {geocoding ? (
                'Geocoding...'
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Geocode Shop (Uses Credits)
                </>
              )}
            </button>
          )}

          {/* Search Mode Toggle */}
          <div className="search-mode-toggle-shopdetail">
            <button
              className={`mode-btn-shopdetail ${searchMode === 'radius' ? 'active' : ''}`}
              onClick={() => setSearchMode('radius')}
            >
              <svg
                className="mode-icon-shopdetail"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              By Shop Radius
            </button>
            <button
              className={`mode-btn-shopdetail ${searchMode === 'location' ? 'active' : ''}`}
              onClick={() => setSearchMode('location')}
            >
              <svg
                className="mode-icon-shopdetail"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              By Location
            </button>
          </div>

          {/* Mode Description */}
          {searchMode === 'radius' ? (
            <p className="card-desc-shopdetail">
              Find addresses within {radiusInput} miles of the shop location.
            </p>
          ) : (
            <p className="card-desc-shopdetail">
              Find addresses within {radiusInput} miles of a specific ZIP code, city, or state.
            </p>
          )}

          {/* Location Search Inputs (only in location mode) */}
          {searchMode === 'location' && (
            <div className="location-search-shopdetail">
              <div className="location-row-shopdetail">
                <div className="location-field-shopdetail">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    value={locationSearch.searchZipCode}
                    onChange={(e) => updateLocationSearch('searchZipCode', e.target.value)}
                    placeholder="e.g., 83864"
                    className="filter-input-shopdetail"
                  />
                </div>
                <div className="location-or-shopdetail">OR</div>
                <div className="location-field-shopdetail">
                  <label>City</label>
                  <input
                    type="text"
                    value={locationSearch.searchCity}
                    onChange={(e) => updateLocationSearch('searchCity', e.target.value)}
                    placeholder="e.g., Sandpoint"
                    className="filter-input-shopdetail"
                  />
                </div>
                <div className="location-field-shopdetail">
                  <label>State</label>
                  <input
                    type="text"
                    value={locationSearch.searchState}
                    onChange={(e) =>
                      updateLocationSearch('searchState', e.target.value.toUpperCase())
                    }
                    placeholder="e.g., ID"
                    maxLength={2}
                    className="filter-input-shopdetail state-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Basic Options — Address Type is fixed to SFH; Sort By/Order removed. */}
          <div className="search-options-shopdetail">
            <div className="option-group-shopdetail">
              <label>Max Records</label>
              {/* Starts at the full postcard balance and is editable DOWNWARD
                  only — anything above the balance is clamped as you type, since
                  the client can't mail more postcards than they bought. The
                  backend enforces the same ceiling regardless. */}
              <input
                type="number"
                min={1}
                max={quotaMax ?? 1000}
                step={1}
                list="maxrecords-options-shopdetail"
                value={filters.maxRecords}
                onChange={(e) => {
                  if (e.target.value === '') return updateFilter('maxRecords', '');
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n)) return undefined;
                  return updateFilter(
                    'maxRecords',
                    quotaMax != null ? Math.min(n, quotaMax) : n
                  );
                }}
                onBlur={() => {
                  // Don't leave the field empty/zero — fall back to the balance.
                  const n = Number(filters.maxRecords);
                  if (!Number.isFinite(n) || n < 1) {
                    updateFilter('maxRecords', quotaMax ?? 100);
                  }
                }}
                className="select-shopdetail"
              />
              <datalist id="maxrecords-options-shopdetail">
                {[5, 10, 25, 50, 100, 200, 500, 1000]
                  .filter((n) => quotaMax == null || n <= quotaMax)
                  .map((n) => (
                    <option key={n} value={n} />
                  ))}
              </datalist>
              {quotaMax != null && (
                <span
                  className={`quota-hint-shopdetail ${outOfPostcards ? 'empty' : ''}`}
                >
                  {outOfPostcards
                    ? 'No postcards left — purchase more in Service Intel'
                    : `${quotaMax} postcard${quotaMax === 1 ? '' : 's'} available`}
                </span>
              )}
            </div>
          </div>

          {/* Advanced Filters - only for Shop Radius mode */}
          {searchMode === 'radius' && (
            <>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="toggle-filters-btn-shopdetail"
              >
                <svg
                  className={`toggle-chevron-shopdetail ${showFilters ? 'open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                {showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
              </button>

              {showFilters && (
                <div className="advanced-filters-shopdetail">
                  {/* Location Filters */}
                  <div className="filter-section-shopdetail">
                    <h4 className="filter-section-title-shopdetail">Location Filters</h4>
                    <div className="filter-row-shopdetail">
                      <div className="filter-group-shopdetail">
                        <label>Include ZIP Codes (comma separated)</label>
                        <input
                          type="text"
                          value={filters.zipCodes}
                          onChange={(e) => updateFilter('zipCodes', e.target.value)}
                          placeholder="e.g., 83864, 83865"
                          className="filter-input-shopdetail"
                        />
                      </div>
                      <div className="filter-group-shopdetail">
                        <label>Exclude ZIP Codes (comma separated)</label>
                        <input
                          type="text"
                          value={filters.excludeZipCodes}
                          onChange={(e) => updateFilter('excludeZipCodes', e.target.value)}
                          placeholder="e.g., 83866"
                          className="filter-input-shopdetail"
                        />
                      </div>
                    </div>
                    <div className="filter-row-shopdetail">
                      <div className="filter-group-shopdetail">
                        <label>Include Cities (comma separated)</label>
                        <input
                          type="text"
                          value={filters.cities}
                          onChange={(e) => updateFilter('cities', e.target.value)}
                          placeholder="e.g., Sandpoint, Ponderay"
                          className="filter-input-shopdetail"
                        />
                      </div>
                      <div className="filter-group-shopdetail">
                        <label>Exclude Cities (comma separated)</label>
                        <input
                          type="text"
                          value={filters.excludeCities}
                          onChange={(e) => updateFilter('excludeCities', e.target.value)}
                          placeholder="e.g., Spokane"
                          className="filter-input-shopdetail"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reset Filters Button */}
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="reset-filters-btn-shopdetail"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </>
          )}

          {/* Already-searched alert: same spot + same-or-lower Max Records. */}
          {areaWarning && (
            <div className="area-warning-shopdetail">
              <span className="area-dot-shopdetail" />
              {areaWarning}
            </div>
          )}

          {/* Where this search will run. In radius mode the dragged search area
              wins over the shop location (see executeSearch); in location mode
              the ZIP/city below is what counts, so we say so explicitly. */}
          <p className="search-area-hint-shopdetail">
            {searchMode === 'radius' ? (
              searchArea ? (
                <>
                  <span className="area-dot-shopdetail" />
                  Searching around the <strong>search area you moved</strong> on the map.
                  Drag it again to pick another spot.
                </>
              ) : (
                <>
                  <span className="area-dot-shopdetail" />
                  Searching around the shop. Drag the{' '}
                  <strong>blue target</strong> on the map to search a different area —
                  you can repeat this as many times as you want.
                </>
              )
            ) : (
              <>
                Searching around the ZIP / city above. To pick a spot on the map instead,
                switch to <strong>By Shop Radius</strong> and drag the blue target.
              </>
            )}
          </p>

          <button
            onClick={handleSearch}
            disabled={
              searching || outOfPostcards || (searchMode === 'radius' && !shop.isGeocoded)
            }
            className="search-btn-shopdetail"
          >
            {searching ? (
              'Searching...'
            ) : outOfPostcards ? (
              'No postcards available'
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                Search Addresses (Uses Credits)
              </>
            )}
          </button>

          {searchMode === 'radius' && !shop.isGeocoded && (
            <p className="warning-shopdetail">Please geocode the shop first for radius search</p>
          )}
            </div>
          </div>
        </div>
      )}

      {/* Send Campaign Modal */}
      <SendCampaignModal
        isOpen={campaignModalOpen}
        selectedBatches={batches.filter((b) => selectedBatchIds.includes(b._id))}
        onClose={() => setCampaignModalOpen(false)}
        onConfirm={handleSendCampaign}
        sending={sendingCampaign}
      />
    </div>
  );
};

export default ShopDetail;
